import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Grid, Checkbox, FormControlLabel, Radio, RadioGroup, FormControl, FormGroup, TextField, CircularProgress, Tooltip, Accordion, AccordionSummary, AccordionDetails, List, ListItem, ListItemText, ListItemButton, Chip, Card as MuiCard, InputAdornment } from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Code as CodeIcon,
  Description as DescriptionIcon,
  Language as LanguageIcon,
  PhoneAndroid as PhoneAndroidIcon,
  Home as HomeIcon,
  Slideshow as SlideShowIcon,
  Chat as ChatIcon,
  BusinessCenter as BusinessCenterIcon,
  Architecture as ArchitectureIcon,
  FilterList as FilterIcon,
  Settings as SettingsIcon,
  ListAlt as ListAltIcon,
  VideoLibrary as VideoLibraryIcon,
  ShoppingCart as ShoppingCartIcon,
  Assignment as AssignmentIcon,
  Refresh as RefreshIcon,
  People as PeopleIcon,
  School as SchoolIcon,
  Image as ImageIcon,
  ExpandMore as ExpandMoreIcon,
  AddTask as AddTaskIcon,
  Update as UpdateIcon,
  Edit as EditIcon,
  ContentCopy as CloneIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  PlayArrow as PlayArrowIcon,
  Search as SearchIcon
} from '@mui/icons-material';

// Import components from our design system
import {
  PageContainer,
  Box,
  Typography,
  Button,
  ProjectListView
} from '../design-system';
import { PageTitleContainer } from '../components/common';
import PermissionGate from '../components/common/PermissionGate';
import FilterPopup from '../components/projects/FilterPopup';

// Import the CreateProject component for editing
import CreateProject from './CreateProject';

// Import services
import projectTypeService from '../services/projectTypeService';
import projectService from '../services/projectService';

// Import hooks
import useProject from '../hooks/useProject';
import useNotification from '../hooks/useNotification';

// Import menu settings component
import ProjectMenuSettings from '../components/projects/ProjectMenuSettings';

/**
 * Projects Page
 * Main page for displaying and managing projects
 * 
 * @returns {React.ReactElement} Projects page component
 */
const Projects = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();
  const [popupProjects, setPopupProjects] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [filterStatus, setFilterStatus] = useState('active');
  const [activeTab, setActiveTab] = useState('active'); // 'active' or 'archived'
  const [searchValue, setSearchValue] = useState('');
  const [selectedLetter, setSelectedLetter] = useState('All');
  const [currentProject, setCurrentProject] = useState(null);
  // Default project types for the creation dialog (fallback if API fails)
  // We no longer need hard-coded project types as we'll use only API data
  const [projectTypes, setProjectTypes] = useState([]);
  const [loadingProjectTypes, setLoadingProjectTypes] = useState(false);
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const [projectToArchive, setProjectToArchive] = useState(null);
  const [archiveAction, setArchiveAction] = useState('archived'); // 'archived' or 'active'

  // Get type and parentId from URL query parameters
  const urlParams = new URLSearchParams(location.search);
  const typeFromUrl = urlParams.get('type') || 'project';
  const parentIdFromUrl = urlParams.get('parentId');

  // Get page title based on type
  const getPageTitle = (type) => {
    switch (type) {
      case 'new-feature':
        return 'New Features';
      case 'change-request':
        return 'Change Requests';
      case 'project':
      default:
        return 'My projects';
    }
  };

  const pageTitle = getPageTitle(typeFromUrl);

  // Use the enhanced project hook with React Query
  const {
    projects,
    projectsPagination,
    isLoadingProjects,
    projectsError,
    projectFilters,
    setFilters,
    updateProject,
    deleteProject,
    cloneProject,
    archiveProject,
    refetchProjects
  } = useProject();
  // Fetch project types from the backend
  const fetchProjectTypes = useCallback(async () => {
    setLoadingProjectTypes(true);
    try {
      const types = await projectTypeService.getProjectTypes();
      console.log('Fetched project types:', types);

      if (types && types.length > 0) {
        // Only use active project types from the API
        const activeTypes = types.filter(type => type.isActive === true);

        // Sort by order
        const sortedTypes = activeTypes.sort((a, b) => (a.order || 99) - (b.order || 99));

        console.log('Using project types from API:', sortedTypes);
        setProjectTypes(sortedTypes);
      } else {
        console.log('No project types returned from API');
        setProjectTypes([]);
      }
    } catch (error) {
      console.error('Error fetching project types:', error);
      showNotification({
        message: `Error loading project types: ${error.message}`,
        type: 'error'
      });
      // Set empty array if there's an error
      setProjectTypes([]);
    } finally {
      setLoadingProjectTypes(false);
    }
  }, [showNotification]);

  // When component mounts, set filter to only show active projects and fetch project types
  useEffect(() => {
    const filters = { status: 'active', type: typeFromUrl };
    // Add parentId to filters if it exists (for new-feature and change-request)
    if (parentIdFromUrl) {
      filters.parentId = parentIdFromUrl;
    }
    setFilters(filters);
    fetchProjectTypes();
  }, [setFilters, fetchProjectTypes, typeFromUrl, parentIdFromUrl]);

  // Handler for create button click - routes based on type
  const handleCreateClick = () => {
    // If type is 'new-feature', redirect to new feature creation page
    if (typeFromUrl === 'new-feature' && parentIdFromUrl) {
      navigate(`/projects/${parentIdFromUrl}/new-feature`);
    } else if (typeFromUrl === 'change-request' && parentIdFromUrl) {
      // Handle change-request similarly if needed
      navigate(`/projects/${parentIdFromUrl}/change-request`);
    } else {
      // For 'project' type, show the create dialog
      setCreateDialogOpen(true);
    }
  };

  // Handler for closing the create dialog
  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
  };

  // Handler for selecting a project type
  const handleProjectTypeSelect = (type) => {
    setCreateDialogOpen(false);
    navigate('/projects/create', { state: { projectType: type } });
  };

  // Handler for changing project status
  const handleProjectStatusChange = async (project, newStatus) => {
    try {
      const projectId = project._id || project.id;
      console.log(`Changing project status: ${projectId} to ${newStatus}`);
      showNotification({
        message: `Updating project ${project.name}...`,
        type: 'info'
      });

      // Use the archiveProject function specifically for archiving
      if (newStatus === 'archived') {
        await archiveProject(projectId);
      } else if (newStatus === 'active' && project.status === 'archived') {
        // Unarchive the project (set status back to active)
        await updateProject({ projectId, projectData: { status: 'active' } });
      } else {
        // For other status changes, use updateProject
        await updateProject({ projectId, projectData: { status: newStatus } });
      }

      // Success notification
      let statusMessage = newStatus;
      if (newStatus === 'archived') {
        statusMessage = 'archived';
      } else if (newStatus === 'active') {
        statusMessage = 'active';
      }

      showNotification({
        message: `Project ${project.name} is now ${statusMessage}`,
        type: 'success'
      });

      // Invalidate all project queries so both Active and Archived tabs refresh
      queryClient.invalidateQueries(['projects']);

    } catch (err) {
      console.error("Error updating project status:", err);
      showNotification({
        message: `Error updating project: ${err.message || "Unknown error"}`,
        type: 'error'
      });
    }
  };

  // Handler for requesting archive/unarchive with confirmation
  const handleArchiveRequest = (project, newStatus) => {
    setProjectToArchive(project);
    setArchiveAction(newStatus);
    setArchiveConfirmOpen(true);
  };

  // Handler for confirming archive/unarchive
  const handleArchiveConfirm = () => {
    if (projectToArchive) {
      handleProjectStatusChange(projectToArchive, archiveAction);
    }
    setArchiveConfirmOpen(false);
    setProjectToArchive(null);
  };

  // Handler for cancelling archive/unarchive
  const handleArchiveCancel = () => {
    setArchiveConfirmOpen(false);
    setProjectToArchive(null);
  };

  // Handler for editing a project
  const handleEditProject = (project) => {
    // Set the current project and open the edit dialog
    console.log('Editing project:', project);
    setCurrentProject(project);
    setEditDialogOpen(true);
  };

  // Handler for closing the edit dialog
  const handleCloseEditDialog = (shouldRefetch = true) => {
    setEditDialogOpen(false);
    setCurrentProject(null);

    // Refetch projects to show updated data
    if (shouldRefetch) {
      refetchProjects();
    }
  };

  // Handler for deleting a project
  const handleDeleteProject = async (project) => {
    try {
      await deleteProject(project._id);
      showNotification({
        message: `Project ${project.name} deleted successfully`,
        type: 'success'
      });
    } catch (err) {
      showNotification({
        message: `Error deleting project: ${err.message}`,
        type: 'error'
      });
    }
  };

  // Handler for cloning a project
  const handleCloneProject = async (project) => {
    try {
      console.log("Cloning project:", project._id);
      showNotification({
        message: `Cloning ${project.type === 'new-feature' ? 'feature' : project.type === 'change-request' ? 'change request' : 'project'} ${project.name}...`,
        type: 'info'
      });

      // Call the cloneProject function with a properly formatted object
      const clonedProject = await cloneProject({
        projectId: project._id,
        options: {
          includeDocs: true,
          includeFeatures: true,
          preserveType: true,  // Preserve the project type
          preserveParent: true  // Preserve the parent relationship
        }
      });

      if (clonedProject && clonedProject._id) {
        showNotification({
          message: `${project.type === 'new-feature' ? 'Feature' : project.type === 'change-request' ? 'Change request' : 'Project'} ${project.name} cloned successfully`,
          type: 'success'
        });

        // If cloning from new-feature or change-request page, stay on the same page and refresh
        if (typeFromUrl === 'new-feature' || typeFromUrl === 'change-request') {
          refetchProjects();
        } else {
          navigate(`/projects/${clonedProject._id}`);
        }
      } else {
        throw new Error("Cloned project data is incomplete");
      }
    } catch (err) {
      console.error("Error cloning project:", err);

      let errorMessage = "Unknown error";
      if (err.message) {
        errorMessage = err.message;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (typeof err === 'string') {
        errorMessage = err;
      }

      showNotification({
        message: `Error cloning: ${errorMessage}`,
        type: 'error'
      });
    }
  };
  // Handler for opening filter dialog - directly fetch all projects for the popup
  const handleOpenFilterDialog = () => {
    // Reset all filter selections
    setSearchValue('');
    setSelectedProjects([]);
    setSelectedLetter('All');

    // Get current status filter
    const currentStatus = projectFilters.status || 'active';

    // Create filters for direct API call
    const apiFilters = {
      status: currentStatus,
      search: '',
      page: 1,
      limit: 100
    };

    console.log('Filter icon clicked: Directly fetching all projects for popup');

    // Show the dialog immediately with loading state
    setFilterDialogOpen(true);

    // Make a direct API call to get fresh projects data for the popup
    projectService.getProjects(apiFilters)
      .then(result => {
        if (result && result.data) {
          console.log(`API call successful: got ${result.data.length} projects for popup`);
          // Update the popup projects state with the API result
          setPopupProjects(result.data);
        } else {
          console.log('API call returned no data, using empty array for popup projects');
          setPopupProjects([]);
        }
      })
      .catch(error => {
        console.error('Error fetching projects for popup:', error);
        // Set empty array on error
        setPopupProjects([]);
      });
  };

  // Handler for closing filter dialog
  const handleCloseFilterDialog = () => {
    setFilterDialogOpen(false);
  };

  // Handler for applying filters
  const handleApplyFilters = (filterData = {}) => {
    // Extract filter data if provided
    const {
      selectedProjects: filterSelectedProjects,
      searchTerm,
      activeAlphabet
    } = filterData;

    // Use provided values or current state
    const projectIds = filterSelectedProjects || selectedProjects;
    const search = searchTerm || searchValue;
    const letter = activeAlphabet || selectedLetter;

    // Build the filters object
    const filters = {
      status: filterStatus,
      search: search,
      page: 1
    };

    // If we have selected projects, add them to the filters
    if (projectIds && projectIds.length > 0) {
      filters.projectIds = projectIds;
      console.log(`Filtering to show ${projectIds.length} selected projects`);
    } else {
      console.log('No projects selected, showing all projects');
    }

    // If a letter is selected, add it to the filters
    if (letter && letter !== 'All') {
      filters.selectedLetter = letter;
      console.log(`Filtering by projects starting with letter: ${letter}`);
    }

    console.log('Applying filters:', filters);

    // Apply the filters
    setFilters(filters);
    setFilterDialogOpen(false);

    // Save current filter state for next time
    setSelectedProjects(projectIds);
    setSearchValue(search);
    setSelectedLetter(letter);
  };

  // Handler for selecting projects in the filter dialog
  const handleProjectSelection = (projectId) => {
    if (selectedProjects.includes(projectId)) {
      setSelectedProjects(selectedProjects.filter(id => id !== projectId));
    } else {
      setSelectedProjects([...selectedProjects, projectId]);
    }
  };

  // Handler for status filter change in the dialog
  const handleStatusFilterChange = (event) => {
    setFilterStatus(event.target.value);
  };

  // Handler for search input change in the dialog
  const handleSearchInputChange = (event) => {
    setSearchValue(event.target.value);
  };

  // Handler for switching between Active and Archived tabs
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setFilterStatus(tab);
    const filters = { status: tab, type: typeFromUrl, page: 1 };
    if (parentIdFromUrl) {
      filters.parentId = parentIdFromUrl;
    }
    setFilters(filters);
  };

  // Create actions for project list header
  // We'll now use icons directly in the page header, so no need for these buttons
  const headerActions = () => (
    <IconButton
      size="medium"
      onClick={handleCreateClick}
      sx={{
        color: 'primary.main',
        visibility: 'visible !important',
        display: 'inline-flex !important',
        opacity: '1 !important',
        zIndex: 10
      }}
    >
      <svg width="32" height="32" viewBox="0 0 32 32">
        <path d="M34,18A16,16,0,1,1,18,2,16,16,0,0,1,34,18Zm-8.41-1.5H19.5V10.41a1.5,1.5,0,0,0-3,0V16.5H10.41a1.5,1.5,0,0,0,0,3H16.5v6.09a1.5,1.5,0,1,0,3,0V19.5h6.09a1.5,1.5,0,1,0,0-3Z" transform="translate(-2 -2)" fill="#475969" />
      </svg>
    </IconButton>
  );

  // Function to get the appropriate icon component based on the icon name from the backend
  const getIconComponent = (iconName) => {
    console.log('Getting icon for:', iconName);
    switch (iconName) {
      case 'CodeIcon': return <CodeIcon />;
      case 'PhoneAndroidIcon': return <PhoneAndroidIcon />;
      case 'LanguageIcon': return <LanguageIcon />;
      case 'HomeIcon': return <HomeIcon />;
      case 'SlideShowIcon': return <SlideShowIcon />;
      case 'DescriptionIcon': return <DescriptionIcon />;
      case 'ChatIcon': return <ChatIcon />;
      case 'ShoppingCartIcon': return <ShoppingCartIcon />;
      case 'AssignmentIcon': return <AssignmentIcon />;
      case 'ListAltIcon': return <ListAltIcon />;
      case 'SettingsIcon': return <SettingsIcon />;
      case 'VideoLibraryIcon': return <VideoLibraryIcon />;
      case 'BusinessCenterIcon': return <BusinessCenterIcon />;
      case 'ArchitectureIcon': return <ArchitectureIcon />;
      case 'PeopleIcon': return <PeopleIcon />;
      case 'SchoolIcon': return <SchoolIcon />;
      case 'ImageIcon': return <ImageIcon />;
      default: return <CodeIcon />;
    }
  };

  return (
    <PageContainer
      fullWidth={true}
      sx={{
        px: 3, // Consistent padding on all sides
        py: 3,
        '& .MuiContainer-root': {
          maxWidth: '100% !important',
          padding: '0 !important'
        },
        display: 'flex',
        flexDirection: 'column',
        height: '100vh', // Full height
        overflow: 'hidden', // Prevent unwanted scrolling
        '& > *': {
          maxWidth: '100%'
        }
      }}
      className="projects-page-container"
    >
      {/* Page Title */}
      <PageTitleContainer
        title={pageTitle}
        rightContent={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <PermissionGate permission="project.dashboard.settings">
              <ProjectMenuSettings />
            </PermissionGate>
            <IconButton
              size="medium"
              onClick={handleOpenFilterDialog}
              sx={{
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'transparent'
                }
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 32.917 29.329">
                <path id="_1652628681582962172" data-name="1652628681582962172" d="M2.5,9.2v1.142L15.724,26.876H16.2V38.531L21.712,34.2V26.876h.477L35.415,10.344V9.2Z" transform="translate(-2.498 -9.202)" fill="#475969"></path>
              </svg>
            </IconButton>
            <PermissionGate permission="header.createproject">
              <IconButton
                size="medium"
                onClick={handleCreateClick}
                sx={{
                  color: 'primary.main',
                  visibility: 'visible !important',
                  display: 'inline-flex !important',
                  opacity: '1 !important',
                  zIndex: 10,
                  '&:hover': {
                    bgcolor: 'transparent'
                  }
                }}
              >
                <svg width="32" height="32" viewBox="0 0 32 32">
                  <path d="M34,18A16,16,0,1,1,18,2,16,16,0,0,1,34,18Zm-8.41-1.5H19.5V10.41a1.5,1.5,0,0,0-3,0V16.5H10.41a1.5,1.5,0,0,0,0,3H16.5v6.09a1.5,1.5,0,1,0,3,0V19.5h6.09a1.5,1.5,0,1,0,0-3Z" transform="translate(-2 -2)" fill="#475969"></path>
                </svg>
              </IconButton>
            </PermissionGate>
          </Box>
        }
        sx={{
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          ml: 0,
          mr: 0,
          mb: 2,
          mt: 0,
          py: '16px' // Exact 16px padding top and bottom
        }}
      />

      {/* Project List - Show Accordion for new-feature/change-request, Grid for normal projects */}
      <Box
        sx={{
          px: (typeFromUrl === 'new-feature' || typeFromUrl === 'change-request') ? 3 : 0,
          width: '100%',
          maxWidth: '100%',
          height: typeFromUrl === 'project' ? 'calc(100vh - 180px)' : 'calc(100vh - 180px)',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'white', // White background for content area only
          margin: '0 auto',
          borderRadius: '4px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          overflow: (typeFromUrl === 'new-feature' || typeFromUrl === 'change-request') ? 'auto' : 'hidden',
          '&::-webkit-scrollbar': {
            width: '8px',
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-track': {
            borderRadius: '10px',
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
            marginTop: '5px',
            marginBottom: '5px',
          },
          '&::-webkit-scrollbar-thumb': {
            borderRadius: '10px',
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.4)',
            },
          },
          '&::-webkit-scrollbar-corner': {
            backgroundColor: 'transparent',
          },
        }}
      >
        <Box sx={{
          px: 5,
          pt: 3
        }}>
          {/* Active / Archived Tabs + Search */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',  flexShrink: 0, px: 2 }}>
            <Box sx={{ display: 'flex', gap: 3 }}>
              {['active', 'archived'].map((tab) => (
                <Box
                  key={tab}
                  onClick={() => handleTabChange(tab)}
                  sx={{
                    px: 2.5,
                    py: 1.5,
                    cursor: 'pointer',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    color: activeTab === tab ? '#1e293b' : '#94a3b8',
                    borderBottom: activeTab === tab ? '2px solid #475969' : '2px solid transparent',
                    mb: '-1px',
                    transition: 'all 0.2s',
                    '&:hover': {
                      color: '#1e293b'
                    }
                  }}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </Box>
              ))}
            </Box>
            <TextField
              size="small"
              placeholder="Search projects..."
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value);
                setFilters({ search: e.target.value });
              }}
              sx={{
                width: '25%',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '4px',
                  color: 'var(--color-blue, #475969)',
                  height: '45px',
                  transition: 'all 0.2s',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#475969',
                    borderWidth: '1px',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#475969',
                    borderWidth: '1px',
                  }
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#4759694d',
                  border: '1px solid #4759694d',
                  borderRadius: '4px',
                },
                '& .MuiInputBase-input': {
                  fontSize: '14px',
                  padding: '12px 14px',
                  color: 'var(--color-blue, #475969)',
                },
                '& .MuiInputBase-input::placeholder': {
                  color: '#a2abb3',
                  opacity: 0.7,
                  fontSize: '14px',
                },
              }}
            />
          </Box>
        </Box>

        {(typeFromUrl === 'new-feature' || typeFromUrl === 'change-request') ? (
          // Accordion View for child projects
          <Box sx={{ py: 3, height: '100%' }}>
            {isLoadingProjects ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : projects && projects.length === 0 ? (
              <Box sx={{
                height: '100%'
              }}>
                <MuiCard sx={{
                  p: 4, textAlign: 'center', boxShadow: 'none !important',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column'
                }}>
                  <Typography variant="h6" gutterBottom>
                    {activeTab === 'archived'
                      ? `No archived ${typeFromUrl === 'new-feature' ? 'features' : 'change requests'}`
                      : `No ${typeFromUrl === 'new-feature' ? 'features' : 'change requests'} yet`
                    }
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {activeTab === 'archived'
                      ? `${typeFromUrl === 'new-feature' ? 'Features' : 'Change requests'} you archive will appear here.`
                      : `Click the "+" button above to create your first ${typeFromUrl === 'new-feature' ? 'feature' : 'change request'}.`
                    }
                  </Typography>
                </MuiCard>
              </Box>
            ) : (
              <Box>
                {projects.map((project, index) => (
                  <Accordion
                    key={project._id}
                    sx={{
                      mb: 2,
                      boxShadow: 'none',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px !important',
                      '&:before': { display: 'none' },
                      '&.Mui-expanded': {
                        margin: '0 0 16px 0'
                      },
                      overflow: 'hidden'
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: '#64748b' }} />}
                      sx={{
                        backgroundColor: '#ffffff',
                        borderBottom: '1px solid #e0e0e0',
                        minHeight: 72,
                        '&:hover': {
                          backgroundColor: '#f8fafc'
                        },
                        '&.Mui-expanded': {
                          minHeight: 72,
                          borderBottom: '1px solid #e0e0e0'
                        },
                        px: 3,
                        py: 1.5
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b', mb: 0.5 }}>
                            {project.name}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Chip
                              label={project.status || 'active'}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: '0.75rem',
                                textTransform: 'capitalize',
                                backgroundColor: '#f1f5f9',
                                color: '#64748b',
                                fontWeight: 500
                              }}
                            />
                            {project.createdAt && (
                              <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                                Created {new Date(project.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <PermissionGate permission="project.dashboard.edit">
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditProject(project);
                                }}
                                sx={{
                                  color: '#64748b',
                                  '&:hover': {
                                    backgroundColor: '#f8fafc',
                                    color: '#1e293b'
                                  }
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </PermissionGate>
                          <PermissionGate permission="project.dashboard.edit">
                            <Tooltip title="Clone">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCloneProject(project);
                                }}
                                sx={{
                                  color: '#64748b',
                                  '&:hover': {
                                    backgroundColor: '#f8fafc',
                                    color: '#1e293b'
                                  }
                                }}
                              >
                                <CloneIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </PermissionGate>
                          <PermissionGate permission="project.dashboard.archive">
                            <Tooltip title={activeTab === 'archived' ? 'Unarchive' : 'Archive'}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleArchiveRequest(project, activeTab === 'archived' ? 'active' : 'archived');
                                }}
                                sx={{
                                  color: '#64748b',
                                  '&:hover': {
                                    backgroundColor: '#f8fafc',
                                    color: '#1e293b'
                                  }
                                }}
                              >
                                {activeTab === 'archived' ? <UnarchiveIcon fontSize="small" /> : <ArchiveIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          </PermissionGate>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(`/projects/${project._id}`, '_blank');
                            }}
                            sx={{
                              px: 1,
                              textTransform: 'none',
                              fontWeight: 600,
                              borderRadius: '6px',
                              boxShadow: 'none',

                            }}
                          >
                            <PlayArrowIcon />
                          </IconButton>
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0, backgroundColor: '#ffffff' }}>
                      <Box sx={{ px: 3, py: 3, borderTop: 'none' }}>
                        {project.description ? (
                          <Box>
                            <Typography variant="body2" sx={{ color: '#64748b', lineHeight: 1.6 }}>
                              <div dangerouslySetInnerHTML={{ __html: project.description }} />
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
                            No description provided
                          </Typography>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            )}
          </Box>
        ) : (
          // Grid View for normal projects
          <ProjectListView
            projects={projects}
            pagination={projectsPagination}
            loading={isLoadingProjects}
            error={projectsError}
            filters={projectFilters}
            onFilterChange={setFilters}
            onStatusChange={handleProjectStatusChange}
            onEdit={handleEditProject}
            onDelete={handleDeleteProject}
            onClone={handleCloneProject}
            headerAction={headerActions}
            title=""
            emptyStateTitle={activeTab === 'archived' ? "No archived projects" : "No projects found"}
            emptyStateDescription={activeTab === 'archived' ? "Projects you archive will appear here. You can unarchive them anytime." : "Create a new project to get started with your product development journey."}
            sx={{
              flex: 1,
              bgcolor: 'white',
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          />
        )}
      </Box>

      {/* Filter Popup */}
      <FilterPopup
        open={filterDialogOpen}
        onClose={handleCloseFilterDialog}
        onApply={handleApplyFilters}
        projects={popupProjects}
        selectedProjects={selectedProjects}
        onProjectSelectionChange={setSelectedProjects}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onAlphabetChange={setSelectedLetter}
        initialAlphabet={selectedLetter}
      />

      {/* Create Project Type Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={handleCloseCreateDialog}
        maxWidth={false}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.7)'
          }
        }}
        PaperProps={{
          sx: {
            borderRadius: 1,
            width: '90%',
            height: '90%',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'hidden',
            margin: 'auto',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
            pb: '14px',
            pt: '14px',
            pl: 5,
            pr: '10px',
            background: 'white'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={600} sx={{ color: '#212529', fontSize: '1.1rem' }}>Create</Typography>
            {loadingProjectTypes && (
              <CircularProgress size={16} sx={{ ml: 1 }} />
            )}
          </Box>
          <IconButton onClick={handleCloseCreateDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 4, py: 5, px: 5, bgcolor: 'white' }}>
          <Box sx={{ px: 0, py: 0, bgcolor: '#fff' }}>
            <Grid
              container
              spacing={5}
            > {/* No spacing in the grid */}
              {/* Loading state */}
              {/* Show spinner alongside project types while loading */}
              {loadingProjectTypes && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', py: 5 }}>
                    <CircularProgress size={40} />
                  </Box>
                </Grid>
              )}

              {/* No project types message */}
              {!loadingProjectTypes && projectTypes.length === 0 && (
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 4
                  }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No project types available
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={fetchProjectTypes}
                      sx={{ mt: 3, py: 1, px: 3 }}
                    >
                      Refresh project types
                    </Button>
                  </Box>
                </Grid>
              )}

              {/* DYNAMIC MAPPING OF PROJECT TYPES */}
              {projectTypes.map((type) => (
                <Grid item xs={12} sm={6} md={3} key={type._id}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 4,
                      bgcolor: '#f5f6f7',
                      color: '#343a40',
                      borderRadius: '4px',
                      height: '160px',
                      width: '100%',
                      maxWidth: '100%',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: '#ebeced'
                      }
                    }}
                    onClick={() => handleProjectTypeSelect(type.key)}
                  >
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        bgcolor: '#4d5666',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2.5,
                        '& .MuiSvgIcon-root': {
                          fontSize: '2rem'
                        }
                      }}
                    >
                      {getIconComponent(type.icon || 'CodeIcon')}
                    </Box>
                    <Typography
                      variant="body1"
                      fontWeight={500}
                      color="#343a40"
                      sx={{ fontSize: '1rem', mt: 1 }}
                    >
                      {type.name}
                    </Typography>
                  </Box>
                </Grid>
              ))}

              {/* Display message when no project types are available */}
              {projectTypes.length === 0 && !loadingProjectTypes && (
                <Grid item xs={12}>
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No project types available. Please check your configuration.
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => handleCloseEditDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            width: '90%',
            height: '90%',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'hidden',
            margin: 'auto',
            display: 'flex',
            flexDirection: 'column',
            backgroundImage: 'none',
            '&::before': { display: 'none' },
            '&::after': { display: 'none' }
          }
        }}
      >
        <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1 }}>
          {currentProject && (
            <Box sx={{ overflow: 'auto', flex: 1 }}>
              <CreateProject
                projectId={currentProject._id}
                project={currentProject} // Pass the project data directly
                editMode={true}
                onClose={handleCloseEditDialog}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Archive/Unarchive Confirmation Dialog */}
      <Dialog
        open={archiveConfirmOpen}
        onClose={handleArchiveCancel}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            minWidth: 400,
            p: 1
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 600 }}>
          {archiveAction === 'archived' ? 'Archive' : 'Unarchive'} {typeFromUrl === 'new-feature' ? 'Feature' : typeFromUrl === 'change-request' ? 'Change Request' : 'Project'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ color: '#475569' }}>
            Are you sure you want to {archiveAction === 'archived' ? 'archive' : 'unarchive'} <strong>{projectToArchive?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleArchiveCancel}
            sx={{
              color: '#64748b',
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleArchiveConfirm}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              bgcolor: archiveAction === 'archived' ? '#475969' : '#1976d2',
              '&:hover': {
                bgcolor: archiveAction === 'archived' ? '#374151' : '#1565c0'
              }
            }}
          >
            {archiveAction === 'archived' ? 'Archive' : 'Unarchive'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default Projects;